// JEPX スポット市場フェーズの判定 (= frontend 単独で UI 表示用に算出)。
// backend が authoritative phase を proto に乗せれば hybrid に切替予定 (= issue #581 T1)。
//
// JEPX スポット市場 (1 日前市場) のタイムライン (JST):
//   - 入札受付開始: 受渡日 11 日前の 0:00 (それより前は 「予約入札」)
//   - 入札締切:  受渡日 1 日前の 10:00
//   - 約定処理:  10:00 〜 10:30
//   - 約定結果公開: 10:30 以降
//   - 受渡:      受渡日 0:00 〜 23:59
//
// スポットはオークション方式なので締切は 「入札締切」 と呼ぶ。 `docs/domain-terms.md`
// は 「ゲートクローズ / GC」 をスポットの締切に使うことを明示的に禁じている
// (GC は時間前市場のコマ別移動締切の語)。
import type { DeadlineDisplay } from '~/components/bidding/common/SubmitConfirmModal'
import {
  formatJstMonthDay,
  formatJstMonthDayHm,
  formatJstTimeHm,
  jstWallClockToInstant,
  jstYmd,
} from '~/models/dateTime'
import { formatGcRemainingTime } from '~/models/gcRemainingTime'

const DAY_MS = 24 * 60 * 60 * 1000

/** 入札締切の JST 壁時計 (受渡日 **前日** の 10:00)。 */
const DEADLINE_HOUR_JST = 10

// [既知の drift / 本 PR の scope 外] spec / `domain-knowledge.md` は約定処理中の終端を
// 「約定結果公表まで (通常 10 分以内・1 時間以上遅れることもある)」 と定めるが、
// 現実装は 30 分固定で倒す。 自社入札の約定結果有無を見る hybrid 判定とセットで直す。
const MATCHING_DURATION_MS = 30 * 60 * 1000

// 入札受付期間は 「受渡日の 11 日前 午前 0 時 〜 受渡日前日 10:00」 (JEPX 取引規程
// 第16条第1項)。 この期間中に日次の閉場は無い (同条第2項は削除済み。 日次入札可能時間帯
// 08:00〜17:00 の旧設計は 2026-09-09 に撤回済み — spec `spot-bidding.md`
// 「### 決定の経緯: スポットの日次入札可能時間帯 (08:00〜17:00) の撤回」)。
// 受付開始前 (11 日前 0:00 より前) は 「予約入札」 で、 札の作成・編集・削除はできる
// (spec AC-BSP-091 / AC-BSP-092)。
//
// [既知の drift / 本 PR の scope 外] spec AC-BSP-092 は「JEPX へは送らず eBalance
// 内部に保持し、 受付開始時刻にサーバーが自動送信する」 と定めるが、 backend の
// Spot 送信経路 (`SpotBidApprovalRequest.Submit` → 常に `BidSendStatusEnqueued`)
// にこの保持・自動送信は未実装で、 承認が通れば受付開始前でも即座に送信が試みられる
// (`BidSendStatusReserved` は Intraday 専用で Spot には現状セットされない)。
// backend の予約保持・自動送信は別 PR #8115 が担当 (本 PR は frontend の表示のみ)。
export type SpotMarketPhase =
  | 'reserved' // 予約入札 (受渡日 11 日前 0:00 JST より前。 AC-BSP-091 / AC-BSP-092)
  | 'open' // 受付中 (受渡日 11 日前 0:00 〜 前日 10:00 JST)
  | 'matching' // 約定処理中 (受渡日前日 10:00 〜 10:30 JST)
  | 'matched' // 約定済 (受渡日前日 10:30 〜 受渡日翌日 0:00。 受渡日当日を含む。 AC-BSP-045)
  | 'completed' // 受渡完了 (受渡日経過後 = 受渡日翌日 0:00 以降)

/** 入札受付開始の前倒し日数 (受渡日の何日前に受付が始まるか)。 */
const ACCEPTANCE_LEAD_DAYS = 11

/**
 * 入札受付開始時刻 = 受渡日の 11 日前 0:00 JST (JEPX 取引規程 第16条第1項)。
 *
 * [[calcSpotPhase]] の `reserved` → `open` 境界そのもので、 予約入札バッジが出す
 * 「受付開始 M/D HH:MM〜」 もこの時刻を指す ([[spotDeadlineAt]] と締切の関係と同じ理由で
 * 1 関数に寄せる)。 境界ちょうど (0:00 丁度) は受付開始済として扱う (取引規程の受付時間は
 * 「11 日前の日の午前 0 時から」 で始点を含む。 backend の予約送信判定も同じ向き)。
 */
export const spotAcceptanceOpenAt = (targetDate: Date): Date =>
  // 11 日前は hour から 11 日ぶんを引いて表現する ([[spotDeadlineAt]] が前日を
  // `hour - 24` で表すのと同じ書き方。 範囲外の hour は `Date.UTC` の桁下がりで
  // 月跨ぎ・年跨ぎも含めて日を戻す = [[jstWallClockToInstant]] の契約)。
  jstWallClockToInstant(targetDate, -ACCEPTANCE_LEAD_DAYS * 24, 0)

/**
 * 入札受付開始時刻の表示 (`M/D HH:MM`)。 予約入札の入札締切状態バッジ
 * (「予約入札 (受付開始 M/D HH:MM〜)」) と、 承認状態バナーの予約入札フェーズの見出し
 * (AC-BSP-067: バッジと同じ元値を使い、 バナー側で算出を再実装しない) が共有する。
 */
export const spotAcceptanceOpenAtLabel = (targetDate: Date): string =>
  formatJstMonthDayHm(spotAcceptanceOpenAt(targetDate))

/**
 * 入札締切時刻 = 受渡日前日 10:00 JST。
 *
 * [[calcSpotPhase]] の `open` → `matching` 境界そのもので、 受付中バッジの
 * カウントダウン (spec AC-BSP-044) もこの時刻を的にする。 締切を 2 箇所で別々に
 * 組み立てると 「バッジは残 00:00:00 なのにフェーズはまだ受付中」 のような
 * 表示同士のズレが生まれるため、 1 関数に寄せて両者の的を同一にする。
 */
export const spotDeadlineAt = (targetDate: Date): Date =>
  // 前日は hour を 24 引いて表現する — 範囲外の hour は
  // `Date.UTC` の桁下がりで日を跨ぐ ([[jstWallClockToInstant]] の契約。 時間前入札の
  // 「コマ開始の 1 時間前」 が前日に跨る計算と同じ書き方)。
  jstWallClockToInstant(targetDate, DEADLINE_HOUR_JST - 24, 0)

/**
 * `now` の時点でまだ入札締切 ([[spotDeadlineAt]]) を過ぎていない、 最も近い受渡日
 * (カレンダー日 Date)。 翌日の締切 (= 今日 10:00 JST) 前なら翌日、 過ぎていれば翌々日。
 * 「今日」 は端末 TZ ではなく JST の暦日で決める ([[jstYmd]])。
 *
 * 失効した札の「複製」(AC-BSP-011「翌営業日向けに再利用できる」) の複製先に使う。
 * 失効札の受渡日は必ず締切後なので、 その日付のまま新規作成すると AC-BSP-013 /
 * AC-BSP-064 (締切後は作成不可) に当たり backend も拒否する。 JEPX スポットは毎日
 * 開場するため、 翌営業日 = 締切前で最も近い受渡日になる。
 */
export const nearestOpenSpotTargetDate = (now: Date): Date => {
  const { year, month, day } = jstYmd(now)
  const tomorrow = new Date(year, month - 1, day + 1)
  if (now.getTime() < spotDeadlineAt(tomorrow).getTime()) return tomorrow
  return new Date(year, month - 1, day + 2)
}

/**
 * 受渡日 `targetDate` の札が `now` の時点で入札締切 ([[spotDeadlineAt]]) を過ぎているか。
 *
 * ステータス列の 12 区分導出 (`deriveSpotBidStatusCategory`、 AC-BSP-047) に渡す締切判定の
 * 1 か所。 通常入札タブ (1 タブ = 1 受渡日) と入札履歴タブ (行ごとに受渡日が違う) が同じ
 * 述語を使い、 両タブとも分単位で更新される `now` (`useNowByMinute`) を渡す。
 * 受渡日が欠けた行 (想定外) は締切後として扱う。 境界ちょうどは締切後
 * ([[calcSpotPhase]] の `open` → `matching` 境界と同じ向き)。
 */
export const isSpotRowPastDeadline = (
  targetDate: Date | undefined,
  now: Date,
): boolean =>
  targetDate === undefined ||
  now.getTime() >= spotDeadlineAt(targetDate).getTime()

/**
 * 入札締切までの残り秒数 (spec AC-BSP-044 のカウントダウン表示用)。
 *
 * 締切到達後は 0 にクランプする (負値を表示側に流さない = 整形側
 * [[formatGcRemainingTime]] と同じ約束)。 残り 0 の状態は
 * `useNowByMinute` (壁時計の分境界に同期) が締切ちょうどにフェーズを
 * `matching` へ倒すため、 実際には一瞬しか出ない。
 */
export const secondsToSpotDeadline = (now: Date, targetDate: Date): number => {
  const diffMs = spotDeadlineAt(targetDate).getTime() - now.getTime()
  return diffMs <= 0 ? 0 : Math.floor(diffMs / 1000)
}

/**
 * 受渡日 (= Date 0:00 JST 想定) に対し now の時点でのフェーズを返す。
 *
 * 例:
 *   - targetDate 2026-05-26 (= 5/26 受渡)、 now 2026-05-14 09:00 JST → 'reserved'
 *   - targetDate 2026-05-26、 now 2026-05-15 00:00 JST (受付開始) → 'open'
 *   - targetDate 2026-05-26、 now 2026-05-25 09:00 JST → 'open'
 *   - targetDate 2026-05-26、 now 2026-05-25 10:15 JST → 'matching'
 *   - targetDate 2026-05-26、 now 2026-05-25 11:00 JST → 'matched'
 *   - targetDate 2026-05-26、 now 2026-05-26 12:00 JST (受渡日当日) → 'matched' (AC-BSP-045)
 *   - targetDate 2026-05-26、 now 2026-05-27 00:00 JST (受渡日経過後) → 'completed'
 */
export const calcSpotPhase = (now: Date, targetDate: Date): SpotMarketPhase => {
  // 境界は JST 壁時計で定義される業務概念なので、 カレンダー日の受渡日を JST 起点の
  // 絶対時刻へ写してから比較する (`now` は既に絶対時刻なので変換不要)。
  const deliveryStartAt = jstMidnightOf(targetDate)
  // 受渡完了 = 「受渡日経過後」 (spec §市場フェーズ) = 受渡日当日の 24 時間が終わった時点。
  const deliveryEndAt = new Date(deliveryStartAt.getTime() + DAY_MS)
  const acceptanceOpenAt = spotAcceptanceOpenAt(targetDate)
  const deadlineAt = spotDeadlineAt(targetDate)
  const matchingEndAt = new Date(deadlineAt.getTime() + MATCHING_DURATION_MS)
  if (now < acceptanceOpenAt) return 'reserved'
  if (now < deadlineAt) return 'open'
  if (now < matchingEndAt) return 'matching'
  if (now < deliveryEndAt) return 'matched'
  return 'completed'
}

/**
 * 入札締切前 (= 札の作成・編集・削除・申請取消・再申請・承認判断ができる) フェーズか。
 * 予約入札と受付中の 2 つ (spec §市場フェーズ / AC-BSP-092)。 画面の各導線はこの判定を
 * 共有し、 フェーズの追加・分割時に 1 箇所だけ直せば済むようにする。
 */
export const isSpotBeforeDeadline = (phase: SpotMarketPhase): boolean =>
  phase === 'reserved' || phase === 'open'

/** カレンダー日 `Date` を JST の同日 0:00 (絶対時刻) として読む。 */
const jstMidnightOf = (d: Date): Date => jstWallClockToInstant(d, 0, 0)

/**
 * カウントダウン表示 (`formatGcRemainingTime`) から締切日時表示へ切り替える閾値
 * (issue #8320、 Des 確定 2026-09-18)。 24 時間を超える残り時間を秒単位で出しても
 * 「23:59:58」 のような値が延々更新されるだけで業務上の意味を持たないため、 それより
 * 先は締切そのものの日時を表示する。
 */
const DEADLINE_COUNTDOWN_THRESHOLD_SECONDS = 24 * 60 * 60

/**
 * 承認申請確認 modal の入札締切までの残り時間表示 (**呼び出し元はこの modal だけ**。
 * issue #8320、 Des 確定 2026-09-18)。
 *
 * 残り [[DEADLINE_COUNTDOWN_THRESHOLD_SECONDS]] (24 時間) 以内は canonical な
 * [[formatGcRemainingTime]] のカウントダウン (`HH:MM:SS`) をそのまま出す。 24 時間を
 * 超える間は秒単位のカウントダウンに意味が無いため、 締切日時そのもの (`M/D HH:MM`、
 * JST 壁時計、 [[formatJstMonthDay]] / [[formatJstTimeHm]]) に切り替える。
 * `formatGcRemainingTime` 自体は 3 画面 (需要想定・時間前入札・スポット) 共有の
 * canonical フォーマットなので変更しない — 呼び出し側のこの関数で分岐を追加する。
 *
 * **page-header の常設バッジ ([[SpotPhaseBadge]]) はこの分岐を使わない。** 常設バッジは
 * 残り時間をそのまま出す場所なので、 24 時間超のレンジの表記は canonical formatter 側の
 * 責務に寄せる (PdM 判断 issue #4855 → spec `intraday-bidding.md` AC-BIN-001
 * 「24 時間以上は N日 HH:MM:SS」、 実装は PR #7999)。 1 回きりの意思決定の場である
 * この modal だけが絶対時刻 (締切日時) を出す、 という役割の違いによる使い分け
 * (issue #8320 の Des 確定 2026-09-18)。
 *
 * 予約入札フェーズ (spec 上の受渡日 11 日前 0:00 より前。 issue #7021 の drift で現実装は
 * 区別しないが、 この関数はどちらのフェーズで呼ばれても同じ規則で判定する) でも締切
 * 日時自体は隠さない — 隠すのは秒単位カウントダウンだけ。
 */
export function formatSpotDeadlineDisplay(
  remainingSeconds: number,
  deadlineAt: Date,
): DeadlineDisplay {
  if (remainingSeconds <= DEADLINE_COUNTDOWN_THRESHOLD_SECONDS) {
    return { kind: 'countdown', time: formatGcRemainingTime(remainingSeconds) }
  }
  return {
    kind: 'datetime',
    date: formatJstMonthDay(deadlineAt),
    time: formatJstTimeHm(deadlineAt),
  }
}
